'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { useCollapsiblePair } from './useCollapsiblePair'
import { cn } from '../lib/cn'

interface SplitAccordionProps {
  leftTitle: string
  rightTitle: string
  left: React.ReactNode
  right: React.ReactNode
  defaultCollapsedSide?: 'left' | 'right' | null
  className?: string
  localStorageKey?: string
}

export const SplitAccordion = ({
  leftTitle,
  rightTitle,
  left,
  right,
  defaultCollapsedSide = null,
  className,
  localStorageKey = 'split-accordion-state',
}: SplitAccordionProps) => {
  const { collapsed, collapse, expand } = useCollapsiblePair(localStorageKey, defaultCollapsedSide)

  return (
    <div className={cn('flex h-full gap-3', className)}>
      <AccordionPanel
        side='left'
        title={leftTitle}
        collapsed={collapsed === 'left'}
        onCollapse={() => collapse('left')}
        onExpand={expand}
      >
        {left}
      </AccordionPanel>

      <AccordionPanel
        side='right'
        title={rightTitle}
        collapsed={collapsed === 'right'}
        onCollapse={() => collapse('right')}
        onExpand={expand}
      >
        {right}
      </AccordionPanel>
    </div>
  )
}

interface AccordionPanelProps {
  side: 'left' | 'right'
  title: string
  collapsed: boolean
  onCollapse: () => void
  onExpand: () => void
  children: React.ReactNode
}

const AccordionPanel = ({ side, title, collapsed, onCollapse, onExpand, children }: AccordionPanelProps) => {
  const ExpandIcon = side === 'left' ? ChevronRight : ChevronLeft
  const CollapseIcon = side === 'left' ? ChevronLeft : ChevronRight
  const collapseIcon = <CollapseIcon className='size-4 opacity-50 transition-opacity group-hover:opacity-100' />

  return (
    <div className={cn('flex flex-col', collapsed ? 'shrink-0' : 'min-w-0 flex-1')}>
      {collapsed ? (
        <button
          type='button'
          onClick={onExpand}
          aria-label={`Expand ${title}`}
          className='flex h-full w-9 cursor-pointer flex-col items-center gap-2 rounded-lg border border-input bg-muted/40 py-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <ExpandIcon className='size-4' />
          <span className='text-sm font-medium [writing-mode:vertical-rl]'>{title}</span>
        </button>
      ) : (
        <button
          type='button'
          onClick={onCollapse}
          aria-label={`Collapse ${title}`}
          className='group flex cursor-pointer items-center justify-between gap-2 rounded-t-lg border border-b-0 border-input bg-muted/40 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground'
        >
          {side === 'left' ? (
            <>
              <span>{title}</span>
              {collapseIcon}
            </>
          ) : (
            <>
              {collapseIcon}
              <span>{title}</span>
            </>
          )}
        </button>
      )}

      {/* Keep children mounted while collapsed so queries and scroll survive. */}
      <div className={cn('min-h-0 flex-1 overflow-auto rounded-b-lg border border-input p-3', collapsed && 'hidden')}>
        {children}
      </div>
    </div>
  )
}
